import React from 'react';
import { FunnelElement } from '../../types/element.types';
import { useFunnelStore } from '../../store/funnelStore';

interface StyleTabProps {
  element: FunnelElement;
}

/**
 * Style Tab Component
 * Allows editing element styles (colors, fonts, spacing, borders, shadows)
 */
const StyleTab: React.FC<StyleTabProps> = ({ element }) => {
  const { updateElementStyles } = useFunnelStore();

  const handleStyleChange = (key: string, value: string) => {
    updateElementStyles(element.id, { [key]: value });
  };

  return (
    <div className="space-y-4">
      {/* Typography */}
      <div>
        <h3 className="text-sm font-semibold text-gray-700 mb-3">Typography</h3>
        
        <div className="space-y-3">
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">
              Font Size
            </label>
            <input
              type="text"
              value={element.styles.fontSize || ''}
              onChange={(e) => handleStyleChange('fontSize', e.target.value)}
              placeholder="16px"
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-[7px] focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">
              Font Weight
            </label>
            <select
              value={element.styles.fontWeight || '400'}
              onChange={(e) => handleStyleChange('fontWeight', e.target.value)}
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-[7px] focus:outline-none focus:ring-2 focus:ring-primary-500"
            >
              <option value="300">Light (300)</option>
              <option value="400">Normal (400)</option>
              <option value="500">Medium (500)</option>
              <option value="600">Semi Bold (600)</option>
              <option value="700">Bold (700)</option>
              <option value="800">Extra Bold (800)</option>
            </select>
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">
              Text Align
            </label>
            <div className="grid grid-cols-4 gap-2">
              {['left', 'center', 'right', 'justify'].map((align) => (
                <button
                  key={align}
                  onClick={() => handleStyleChange('textAlign', align)}
                  className={`
                    px-2 py-1.5 text-xs border rounded-[7px] transition-all
                    ${
                      element.styles.textAlign === align
                        ? 'border-primary-500 bg-primary-50 text-primary-700'
                        : 'border-gray-300 hover:border-gray-400'
                    }
                  `}
                >
                  {align}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">
              Line Height
            </label>
            <input
              type="text"
              value={element.styles.lineHeight || ''}
              onChange={(e) => handleStyleChange('lineHeight', e.target.value)}
              placeholder="1.6"
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-[7px] focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
          </div>
        </div>
      </div>

      {/* Colors */}
      <div>
        <h3 className="text-sm font-semibold text-gray-700 mb-3">Colors</h3>
        
        <div className="space-y-3">
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">
              Text Color
            </label>
            <div className="flex gap-2">
              <input
                type="color"
                value={element.styles.color || '#000000'}
                onChange={(e) => handleStyleChange('color', e.target.value)}
                className="w-12 h-10 border border-gray-300 rounded-[7px] cursor-pointer"
              />
              <input
                type="text"
                value={element.styles.color || ''}
                onChange={(e) => handleStyleChange('color', e.target.value)}
                placeholder="#000000"
                className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded-[7px] focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
            </div>
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">
              Background Color
            </label>
            <div className="flex gap-2">
              <input
                type="color"
                value={element.styles.backgroundColor || '#ffffff'}
                onChange={(e) => handleStyleChange('backgroundColor', e.target.value)}
                className="w-12 h-10 border border-gray-300 rounded-[7px] cursor-pointer"
              />
              <input
                type="text"
                value={element.styles.backgroundColor || ''}
                onChange={(e) => handleStyleChange('backgroundColor', e.target.value)}
                placeholder="#ffffff"
                className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded-[7px] focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Spacing */}
      <div>
        <h3 className="text-sm font-semibold text-gray-700 mb-3">Spacing</h3>
        
        <div className="space-y-3">
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">
              Padding
            </label>
            <input
              type="text"
              value={element.styles.padding || ''}
              onChange={(e) => handleStyleChange('padding', e.target.value)}
              placeholder="10px 20px"
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-[7px] focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">
              Margin
            </label>
            <input
              type="text"
              value={element.styles.margin || ''}
              onChange={(e) => handleStyleChange('margin', e.target.value)}
              placeholder="10px 0px"
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-[7px] focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
          </div>
        </div>
      </div>

      {/* Border */}
      <div>
        <h3 className="text-sm font-semibold text-gray-700 mb-3">Border</h3>
        
        <div className="space-y-3">
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">
              Border
            </label>
            <input
              type="text"
              value={element.styles.border || ''}
              onChange={(e) => handleStyleChange('border', e.target.value)}
              placeholder="1px solid #000"
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-[7px] focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">
              Border Radius
            </label>
            <input
              type="text"
              value={element.styles.borderRadius || ''}
              onChange={(e) => handleStyleChange('borderRadius', e.target.value)}
              placeholder="8px"
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-[7px] focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
          </div>
        </div>
      </div>

      {/* Effects */}
      <div>
        <h3 className="text-sm font-semibold text-gray-700 mb-3">Effects</h3>
        
        <div className="space-y-3">
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">
              Box Shadow
            </label>
            <input
              type="text"
              value={element.styles.boxShadow || ''}
              onChange={(e) => handleStyleChange('boxShadow', e.target.value)}
              placeholder="0 2px 4px rgba(0,0,0,0.1)"
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-[7px] focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">
              Opacity
            </label>
            <input
              type="range"
              min="0"
              max="1"
              step="0.1"
              value={element.styles.opacity || '1'}
              onChange={(e) => handleStyleChange('opacity', e.target.value)}
              className="w-full"
            />
            <div className="text-xs text-gray-500 text-right">{element.styles.opacity || '1'}</div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StyleTab;

